import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canRetestControls, fmtDate } from "@/components/grc/shared";
import { RetestControlModal } from "@/components/grc/RetestControlModal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Paperclip, RotateCcw, Info } from "lucide-react";

const PILLARS = Array.from({ length: 15 }, (_, i) => `P${i + 1}`);

const TESTING_BADGE = {
  "Assessment-Passed": "bg-green-100 text-green-700 border border-green-200",
  "Failed-Needs-Retest": "bg-red-100 text-red-700 border border-red-200",
  "User-Retested-Pass": "bg-green-700 text-white",
  "User-Retested-Fail": "bg-red-800 text-white",
};

const GAP_TAG_CHIP = {
  PASS: "bg-green-50 text-green-700 border-green-200",
  MINOR: "bg-yellow-50 text-yellow-700 border-yellow-200",
  MAJOR: "bg-amber-50 text-amber-700 border-amber-200",
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_OPTIONS = [
  "Assessment-Passed",
  "Failed-Needs-Retest",
  "User-Retested-Pass",
  "User-Retested-Fail",
];

const truncate = (s, n = 60) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "—");

export const ControlsTracker = ({ assessmentId }) => {
  const { user } = useAuth();
  const canRetest = canRetestControls(user?.role);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pillar, setPillar] = useState("all");
  const [testingStatus, setTestingStatus] = useState("all");
  const [retesting, setRetesting] = useState(null);

  const fetchControls = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/grc/${assessmentId}/controls`);
      setControls(data.controls || []);
    } catch {
      setControls([]);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    fetchControls();
  }, [fetchControls]);

  const statusCounts = useMemo(() => {
    const counts = {};
    controls.forEach((c) => {
      counts[c.testing_status] = (counts[c.testing_status] || 0) + 1;
    });
    return counts;
  }, [controls]);

  const filtered = useMemo(
    () =>
      controls.filter(
        (c) =>
          (pillar === "all" || c.pillar === pillar) &&
          (testingStatus === "all" || c.testing_status === testingStatus)
      ),
    [controls, pillar, testingStatus]
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 space-y-4" data-testid="controls-tracker">
      <div className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="controls-tracker-library-banner">
        <span className="text-slate-600">These are the App A assessment question-controls (read-only scores). Operational controls, procedures, executions and testing live in the Control Library, which uses the same control repository.</span>
        <a href="/controls?tab=library" className="shrink-0 font-semibold text-[#008080] hover:underline" data-testid="open-control-library-link">Open Control Library</a>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="controls-status-chips">
          <button
            onClick={() => setTestingStatus("all")}
            data-testid="controls-chip-all"
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
              testingStatus === "all"
                ? "bg-[#003366] text-white border-[#003366]"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
            }`}
          >
            All ({controls.length})
          </button>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setTestingStatus(s)}
              data-testid={`controls-chip-${s.toLowerCase()}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                testingStatus === s
                  ? `${TESTING_BADGE[s]} border-transparent`
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
              }`}
            >
              {s.replace(/-/g, " ")} ({statusCounts[s] || 0})
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <Select value={pillar} onValueChange={setPillar}>
            <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="controls-pillar-filter">
              <SelectValue placeholder="Pillar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pillars</SelectItem>
              {PILLARS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-14 text-center text-sm text-slate-500" data-testid="controls-empty-state">
          No controls match your filters
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="controls-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Q#</TableHead>
                <TableHead className="text-xs">Pillar</TableHead>
                <TableHead className="text-xs min-w-[200px]">Question Text</TableHead>
                <TableHead className="text-xs">Testing Status</TableHead>
                <TableHead className="text-xs">Score</TableHead>
                <TableHead className="text-xs">Last Tested</TableHead>
                <TableHead className="text-xs">Evidence</TableHead>
                {canRetest && <TableHead className="text-xs text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.control_id} data-testid={`control-row-${c.question_id}`}>
                  <TableCell className="font-mono text-xs text-slate-800">{c.question_id}</TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded border border-slate-300 text-[11px] font-mono text-slate-600">
                      {c.pillar}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-700">
                    {c.question_text && c.question_text.length > 60 ? (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">{truncate(c.question_text)}</span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[360px] text-xs">{c.question_text}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      truncate(c.question_text)
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${TESTING_BADGE[c.testing_status] || "bg-slate-100 text-slate-600"}`}>
                      {c.testing_status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-slate-800">{c.current_score ?? "—"}</span>
                      {c.gap_tag && (
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${GAP_TAG_CHIP[c.gap_tag] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                          {c.gap_tag}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                    {fmtDate(c.last_user_test_at || c.last_tested_at)}
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center gap-1 text-xs text-slate-500"
                      data-testid={`control-evidence-count-${c.question_id}`}
                    >
                      <Paperclip size={13} />
                      {c.evidence_count || 0}
                    </span>
                  </TableCell>
                  {canRetest && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-[#008080] hover:text-[#1a51db]"
                        onClick={() => setRetesting(c)}
                        data-testid={`control-retest-button-${c.question_id}`}
                      >
                        <RotateCcw size={13} className="mr-1" /> Retest
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 border-t border-slate-100 pt-3" data-testid="controls-footer-note">
        <Info size={12} />
        <span>P9 (SDF Readiness) score sourced from SDF Module B assessment</span>
      </div>

      <RetestControlModal
        control={retesting}
        open={!!retesting}
        onClose={() => setRetesting(null)}
        onSaved={() => {
          setRetesting(null);
          fetchControls();
        }}
      />
    </div>
  );
};
